import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion, Variants, useMotionValue, useTransform, useSpring } from 'framer-motion';
import ScatterText from '../common/ScatterText';
import TypeWriter from '../common/TypeWriter/TypeWriter';
import Icon from '../common/Icon';
import './Hero.css';
import './hero-responsive.css';

interface TerminalItem {
  cmd: string;
  output: string;
}

const Hero: React.FC = () => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Smooth springs for 3D tilt
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [12, -12]), { stiffness: 90, damping: 18 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-12, 12]), { stiffness: 90, damping: 18 });

  // Parallax offsets for floating badges
  const badge1X = useSpring(useTransform(x, [-0.5, 0.5], [-20, 20]), { stiffness: 70, damping: 22 });
  const badge1Y = useSpring(useTransform(y, [-0.5, 0.5], [-20, 20]), { stiffness: 70, damping: 22 });
  
  const badge2X = useSpring(useTransform(x, [-0.5, 0.5], [25, -25]), { stiffness: 70, damping: 22 });
  const badge2Y = useSpring(useTransform(y, [-0.5, 0.5], [25, -25]), { stiffness: 70, damping: 22 });
  
  const badge3X = useSpring(useTransform(x, [-0.5, 0.5], [-30, 30]), { stiffness: 70, damping: 22 });
  const badge3Y = useSpring(useTransform(y, [-0.5, 0.5], [10, -10]), { stiffness: 70, damping: 22 });

  // Grid background slight shift
  const gridX = useSpring(useTransform(x, [-0.5, 0.5], [-10, 10]), { stiffness: 80, damping: 20 });
  const gridY = useSpring(useTransform(y, [-0.5, 0.5], [-10, 10]), { stiffness: 80, damping: 20 });

  // Aurora background blob shifts
  const blob1X = useSpring(useTransform(x, [-0.5, 0.5], [35, -35]), { stiffness: 50, damping: 25 });
  const blob1Y = useSpring(useTransform(y, [-0.5, 0.5], [35, -35]), { stiffness: 50, damping: 25 });
  
  const blob2X = useSpring(useTransform(x, [-0.5, 0.5], [-25, 25]), { stiffness: 50, damping: 25 });
  const blob2Y = useSpring(useTransform(y, [-0.5, 0.5], [-25, 25]), { stiffness: 50, damping: 25 });

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left - width / 2;
    const mouseY = e.clientY - rect.top - height / 2;
    x.set(mouseX / width);
    y.set(mouseY / height);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  // Interactive Terminal Logic
  const [history, setHistory] = useState<TerminalItem[]>([
    { cmd: '', output: 'Welcome to Rahim\'s Terminal. Type "help" to start.' }
  ]);
  const [inputVal, setInputVal] = useState('');
  const terminalBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (terminalBodyRef.current) {
      terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
    }
  }, [history]);

  const handleTerminalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputVal.trim().toLowerCase();
    if (!trimmed) return;

    let output = '';
    switch (trimmed) {
      case 'help':
        output = 'Available commands:\n  about     - Learn who I am\n  skills    - Check my core stack\n  projects  - View highlights\n  education - See fast lahore details\n  clear     - Wipe screen logs';
        break;
      case 'about':
        output = 'Muhammad Rahim\n--------------\nCS Student at FAST Lahore. Designing creative, high-fidelity interfaces and machine learning systems. Focused on feature engineering and UX.';
        break;
      case 'skills':
        output = 'Languages: Python, C++, SQL, HTML/JS\nFrontend : React, Framer Motion, Bootstrap\nBackend  : FastAPI, Django, SQLite\nAI/ML    : Pandas, NumPy, Scikit-learn, Seaborn';
        break;
      case 'projects':
        output = 'Selected Projects:\n  - ClankerFUED  — Clan-based web battle system\n  - MediSync     — Healthcare scheduling platform\n  - Shopverse    — E-commerce fullstack app\n  - Tafakkur     — Islamic reflection & journaling app';
        break;
      case 'education':
        output = 'FAST NUCES, Lahore\n------------------\nDegree: BS in Computer Science\nFocus : Artificial Intelligence & Full-stack Engineering';
        break;
      case 'clear':
        setHistory([]);
        setInputVal('');
        return;
      default:
        output = `Command not found: "${inputVal}". Type "help" for instructions.`;
    }

    setHistory(prev => [...prev, { cmd: inputVal, output }]);
    setInputVal('');
  };

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        delay: 0.15,
        when: "beforeChildren",
        staggerChildren: 0.12
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { y: 25, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 90,
        damping: 16
      }
    }
  };

  return (
    <div 
      className="hero-section"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background Aurora Blobs */}
      <div className="aurora-container">
        <motion.div 
          className="aurora-blob aurora-blob-1"
          style={{ x: blob1X, y: blob1Y }}
        />
        <motion.div 
          className="aurora-blob aurora-blob-2"
          style={{ x: blob2X, y: blob2Y }}
        />
        <motion.div 
          className="aurora-blob aurora-blob-3"
          animate={{
            scale: [1, 1.12, 0.96, 1],
            x: [0, 20, -15, 0],
            y: [0, -30, 15, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Tech Grid Overlay */}
      <motion.div 
        className="tech-grid-overlay" 
        style={{ x: gridX, y: gridY }}
      />

      <Container>
        <motion.div 
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Row className="align-items-center">
            <Col lg={7} md={12} className="mb-5 mb-lg-0">
              <motion.div variants={itemVariants} className="hero-name-container">
                <ScatterText 
                  text={"Hello, I'm Muhammad Rahim"}
                  className="hero-scatter-text"
                />
              </motion.div>
              
              <motion.div className="hero-subtitle" variants={itemVariants}>
                <span className="hero-role-inline">I'm a&nbsp;
                  <TypeWriter 
                    words={[
                      "CS Student",
                      "Web Developer",
                      "Software Engineer",
                      "UI/UX Enthusiast",
                      "Data Scientist",
                      "Machine Learning Engineer",
                      "Problem Solver"
                    ]}
                    typingSpeed={100}
                    deletingSpeed={50}
                    delayBetween={1500}
                  />
                </span>
              </motion.div>
              
              <motion.p className="hero-description" variants={itemVariants}>
                A Computer Science student at FAST Lahore with a passion for building creative digital solutions. Skilled in Python, C++, SQL, React, Django, and modern web technologies, I also explore AI/ML with tools like Pandas, NumPy, Matplotlib, Seaborn, and Scikit-learn, currently focusing on feature engineering. I enjoy blending logic with creativity to craft projects that are functional, engaging, and impactful.
              </motion.p>
              
              <motion.div variants={itemVariants} className="hero-buttons-container mb-4">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="d-inline-block"
                >
                  <Link to="/projects">
                    <Button className="hero-button btn-primary me-3">
                      View Projects
                    </Button>
                  </Link>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="d-inline-block"
                >
                  <Link to="/contact">
                    <Button 
                      className="hero-button-outline btn-outline-primary"
                    >
                      Contact Me
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>

              {/* Interactive CLI Terminal Widget */}
              <motion.div variants={itemVariants}>
                <div className="terminal-card">
                  <div className="terminal-header">
                    <div className="terminal-buttons">
                      <div className="terminal-dot dot-red"></div>
                      <div className="terminal-dot dot-yellow"></div>
                      <div className="terminal-dot dot-green"></div>
                    </div>
                    <div className="terminal-title">guest@rahim-portfolio: ~</div>
                  </div>
                  <div 
                    ref={terminalBodyRef}
                    className="terminal-body" 
                    onClick={() => document.getElementById('terminal-input')?.focus()}
                  >
                    <div className="terminal-history">
                      {history.map((item, idx) => (
                        <div key={idx} className="terminal-history-item">
                          {item.cmd && (
                            <div className="terminal-line">
                              <span className="terminal-prompt">guest@rahim:~$</span>
                              <span className="terminal-user-command">{item.cmd}</span>
                            </div>
                          )}
                          <div className="terminal-output-line">{item.output}</div>
                        </div>
                      ))}
                    </div>
                    <form onSubmit={handleTerminalSubmit} className="terminal-input-form">
                      <span className="terminal-prompt">guest@rahim:~$</span>
                      <input
                        id="terminal-input"
                        type="text"
                        value={inputVal}
                        onChange={e => setInputVal(e.target.value)}
                        className="terminal-text-input"
                        autoComplete="off"
                      />
                    </form>
                  </div>
                </div>
              </motion.div>
            </Col>
            
            <Col lg={5} md={12} className="d-flex justify-content-center">
              <motion.div 
                className="card-3d-wrapper"
                variants={itemVariants}
              >
                {/* 3D Interactive Card */}
                <motion.div 
                  className="interactive-3d-card"
                  style={{ rotateX, rotateY }}
                >
                  {/* Morphing Tech Core Replacement for Logo */}
                  {/* Tech Orbit Core Replacement */}
                  <div className="tech-orbit-container">
                    <div className="orbit-glow-bg" />
                    <div className="orbit-system">
                      <div className="orbit-core">
                        <Icon icon="FaBrain" className="orbit-core-icon" />
                      </div>

                      {/* Concentric Orbiting Rings */}
                      <motion.div 
                        className="orbit-ring ring-outer"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      >
                        <motion.div 
                          className="orbit-node node-1"
                          animate={{ rotate: -360 }}
                          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        >
                          <Icon icon="FaReact" />
                        </motion.div>
                        <motion.div 
                          className="orbit-node node-2"
                          animate={{ rotate: -360 }}
                          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        >
                          <Icon icon="FaPython" />
                        </motion.div>
                      </motion.div>

                      <motion.div 
                        className="orbit-ring ring-middle"
                        animate={{ rotate: -360 }}
                        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                      >
                        <motion.div 
                          className="orbit-node node-3"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                        >
                          <Icon icon="FaDatabase" />
                        </motion.div>
                        <motion.div 
                          className="orbit-node node-4"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                        >
                          <Icon icon="FaCode" />
                        </motion.div>
                      </motion.div>
                    </div>
                  </div>
                  
                  <div className="card-details">
                    <h3 className="card-title">Muhammad Rahim</h3>
                    <p className="card-subtitle">CS Student & Developer</p>
                  </div>
                  
                  {/* Parallax Floating Badges */}
                  <motion.div 
                    className="parallax-badge badge-fast"
                    style={{ x: badge1X, y: badge1Y }}
                  >
                    <Icon icon="FaGraduationCap" />
                    <span>FAST Lahore</span>
                  </motion.div>

                  <motion.div 
                    className="parallax-badge badge-lang"
                    style={{ x: badge2X, y: badge2Y }}
                  >
                    <Icon icon="FaReact" />
                    <span>React / Django</span>
                  </motion.div>

                  <motion.div 
                    className="parallax-badge badge-ml"
                    style={{ x: badge3X, y: badge3Y }}
                  >
                    <Icon icon="FaBrain" />
                    <span>ML / AI</span>
                  </motion.div>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </div>
  );
};

export default Hero;
